import { batch } from "omi";
import { cloneDeep, shuffle } from "lodash-es";
import isEmpty from "lodash-es/isEmpty";

import { Option, QuizStatus } from "@/wc/editor/components/nw-quiz/models";
import { getAttemptDate } from "@/wc/editor/components/nw-quiz/utils.ts";
import { FeatureSlice } from "@/store/features";
import { ProjectSlice } from "@/store/project";
import { UserSlice } from "@/store/user";
import { networkSignal, NetworkSignal } from "@/utils/signals";
import { createStore } from "@/utils/store";
import { Plan, Question, QuestionStatus, Quiz, QuizAttempt, FeatureKey } from "@/api";
import { Store } from "@/store";
import { initApi } from "@/utils";

type State = {
  activeQuestionId: string;
  quizStatus: QuizStatus;
  quizDTO: Quiz | undefined;
};

type NetworkState = {
  quiz: NetworkSignal<Quiz>;
  completeQuizMutation: NetworkSignal<QuizAttempt>;
};

type Actions = {
  fetchQuiz: (regenerate?: boolean) => Promise<Quiz | undefined>;
  getQuiz: () => Quiz | undefined;
  startQuiz: () => void;

  hasReachedQuizLimit: () => boolean;
  generateNewQuiz: () => Promise<void>;
  getActiveQuestion: () => { question: Question; index: number } | undefined;
  goToNextQuestion: () => void;
  checkAnswer: (question: Question, option: Option) => void;
  completeQuiz: (callback?: (attempt: QuizAttempt) => void) => void;
  isLastQuestion: () => boolean;
  getLastAttemptStats: () => { passed: boolean; date: string } | undefined;
};

type Slices = {
  projectSlice: ProjectSlice;
  userSlice: UserSlice;
  featureSlice: FeatureSlice;
};

export function createQuizStore() {
  const projectSlice = Store.project;
  const userSlice = Store.user;
  const featureSlice = Store.features;

  return createStore<State, NetworkState, Actions, Slices>({
    initialState: {
      activeQuestionId: "",
      quizStatus: QuizStatus.NotStarted,
      quizDTO: undefined,
    },
    networkState: {
      quiz: networkSignal<Quiz>({ retry: { delayMs: 500, backoffMultiplier: 2, maxAttempts: 3 } }),
      completeQuizMutation: networkSignal<QuizAttempt>({
        retry: { delayMs: 500, backoffMultiplier: 2, maxAttempts: 3 },
      }),
    },
    slices: () => ({
      projectSlice,
      userSlice,
      featureSlice,
    }),
    actions: ({ store, networkState, slices }) => {
      const api = initApi();

      const hasReachedQuizLimit = () => {
        const limit = slices.featureSlice.getFeatureLimitObject(FeatureKey.QUIZ_COMPLETION);
        if (!limit) {
          return false;
        }
        
        const plan = slices.userSlice.isPro() ? Plan.PRO : Plan.FREE;
        return (limit.current ?? 0) >= (limit.limit[plan] ?? 0);
      };

      const getQuiz = () => networkState.quiz.signal.data.value;
      const setQuestionStatus = (question: Question, status: QuestionStatus) => {
        const questions = networkState.quiz.signal.data.value?.questions;
        const questionToUpdate = questions?.find(q => q.id === question.id);
        if (questionToUpdate) {
          questionToUpdate.status = status;
          networkState.quiz.signal.data.update();
        }
      };
      const fetchQuiz = async (regenerate = false) => {
        const projectId = slices.projectSlice.id.value;
        const trackId = slices.projectSlice.getSelectedTrackId();

        if (projectId == null || trackId == null || hasReachedQuizLimit()) {
          return;
        }

        return await networkState.quiz.run(async () => {
          const quiz = await api.getQuiz({ projectId, regenerate });
          if (quiz.attempts == null) {
            quiz.attempts = [];
          }

          // Create a deep clone of the quiz to preserve the original data
          store.quizDTO.value = cloneDeep(quiz);
          return quiz;
        });
      };

      return {
        getQuiz,
        fetchQuiz,
        hasReachedQuizLimit,
        startQuiz: () => {
          const quiz = cloneDeep(store.quizDTO.value);
          const questions = shuffle(quiz?.questions);

          if (quiz != null && questions != null && questions.length > 0) {
            store.activeQuestionId.value = questions[0].id;
            quiz.questions = questions;
          }

          networkState.quiz.signal.data.value = quiz;
        },
        generateNewQuiz: async () => {
          const quiz = await fetchQuiz(true);

          if (quiz == null) {
            return;
          }

          batch(() => {
            store.activeQuestionId.value = "";
            store.quizStatus.value = QuizStatus.NotStarted;
          });
        },
        getActiveQuestion: () => {
          const quiz = networkState.quiz.signal.data.value;

          if (quiz == null) {
            return undefined;
          }

          const questions = quiz!.questions;
          const index = questions.findIndex(question => question.id === store.activeQuestionId.value);

          return index != -1 ? { question: questions[index], index } : undefined;
        },
        goToNextQuestion: () => {
          const quiz = networkState.quiz.signal.data.value;
          const questions = quiz!.questions;
          const currentQuestionIndex = questions.findIndex(question => question.id === store.activeQuestionId.value);

          store.activeQuestionId.value = questions[currentQuestionIndex + 1]?.id;
        },
        checkAnswer: (question, option) => {
          const status = option.isAnswer ? QuestionStatus.QUESTION_CORRECT : QuestionStatus.QUESTION_INCORRECT;
          setQuestionStatus(question, status);
          return status;
        },
        completeQuiz: async (callback?: (attempt: QuizAttempt) => void) => {
          const projectId = slices.projectSlice.id.value;
          const trackId = slices.projectSlice.getSelectedTrackId();

          if (projectId == null || trackId == null) {
            return;
          }

          const quiz = networkState.quiz.signal.data.value;
          if (quiz == null) {
            return;
          }

          const correctAnswers = quiz.questions.reduce((acc, question) => {
            if (question.status === QuestionStatus.QUESTION_CORRECT) {
              return acc + 1;
            }
            return acc;
          }, 0);

          const completedAt = new Date();
          const quizAttempt: QuizAttempt = { score: correctAnswers, completedAt };
          const quizId = quiz.id;

          await networkState.completeQuizMutation.run(async () => {
            return await api.completeQuiz({
              projectId,
              quizId,
              quizAttempt,
            });
          });

          const wasAllCorrect = quiz.questions.every(question => question.status === QuestionStatus.QUESTION_CORRECT);

          if (quiz.attempts != null) {
            quiz.attempts.push(quizAttempt);
          } else {
            quiz.attempts = [quizAttempt];
          }

          // Update feature limit before the batch update
          const featureLimit = slices.featureSlice.getFeatureLimitObject(FeatureKey.QUIZ_COMPLETION);
          if (featureLimit) {
            const currentLimit = featureLimit.current ?? 0;
            slices.featureSlice.updateFeatureLimitCurrent(FeatureKey.QUIZ_COMPLETION, currentLimit + 1);
          }

          batch(() => {
            store.activeQuestionId.value = "";

            if (wasAllCorrect) {
              store.quizStatus.value = QuizStatus.Passed;
            } else {
              store.quizStatus.value = QuizStatus.BelowPassingScore;
            }

            networkState.quiz.signal.data.value = quiz;
            callback?.(quizAttempt);
          });
        },
        isLastQuestion: () => {
          const quiz = networkState.quiz.signal.data.value;
          const questions = quiz!.questions;
          const currentQuestionIndex = questions.findIndex(question => question.id === store.activeQuestionId.value);

          return currentQuestionIndex === questions.length - 1;
        },
        getLastAttemptStats: () => {
          const quiz = getQuiz();
          const attempts = quiz?.attempts;

          if (attempts == null || isEmpty(attempts)) {
            return undefined;
          }

          const lastAttempt = attempts[attempts.length - 1];
          const date = getAttemptDate(lastAttempt);
          const passed = lastAttempt.score > 0 && lastAttempt.score === quiz?.questions.length;

          return {
            passed,
            date,
          };
        },
      };
    },
  });
}

export type QuizStore = ReturnType<typeof createQuizStore>;
